import { useState, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import {
  Page,
  Layout,
  Card,
  ResourceList,
  ResourceItem,
  Text,
  Badge,
  Filters,
  TextField,
  Select,
  Spinner,
  Pagination,
  Avatar,
  InlineStack,
  BlockStack,
  Banner,
  Icon
} from '@shopify/polaris'
import { StoreMajor, GlobeMajor } from '@shopify/polaris-icons'
import { format } from 'date-fns'

import { useAuth } from '@/contexts/AuthContext'
import { conversationApi, storeApi } from '@/services/api'
import StoreSwitcher from '@/components/StoreSwitcher'
import StoreFilter from '@/components/StoreFilter'
import type { ConversationStatus, ConversationFilters } from '@/types'

// ============================================================
// CONVERSATIONS PAGE (Multi-Account Support)
// ============================================================

export default function ConversationsPage() {
  const {
    user,
    stores,
    activeStoreId,
    isUnifiedView,
    hasMultipleStores,
    accessibleStoreIds
  } = useAuth()
  const navigate = useNavigate()

  // Filters state
  const [filters, setFilters] = useState<ConversationFilters>({
    storeId: activeStoreId || undefined,
    page: 1,
    limit: 20
  })
  const [searchValue, setSearchValue] = useState('')
  const [selectedStoreIds, setSelectedStoreIds] = useState<string[]>([])

  // Determine which API to use based on view mode
  const isUnified = isUnifiedView || selectedStoreIds.length > 1

  // Fetch conversations
  const { data: conversationsData, isLoading } = useQuery({
    queryKey: ['conversations', filters, isUnified, selectedStoreIds],
    queryFn: async () => {
      if (isUnified) {
        // Use unified endpoint for multi-store view
        const storeIdsParam = selectedStoreIds.length > 0
          ? selectedStoreIds.join(',')
          : undefined

        return conversationApi.getUnifiedConversations({
          ...filters,
          storeIds: storeIdsParam,
          storeId: undefined // Don't send single storeId
        })
      } else {
        // Use regular endpoint for single store
        return conversationApi.getConversations({
          ...filters,
          storeId: activeStoreId || accessibleStoreIds[0]
        })
      }
    },
    enabled: accessibleStoreIds.length > 0
  })

  // Fetch unified stats (only when in unified view)
  const { data: statsData } = useQuery({
    queryKey: ['conversations', 'unified', 'stats', selectedStoreIds],
    queryFn: () => conversationApi.getUnifiedStats({
      storeIds: selectedStoreIds.length > 0 ? selectedStoreIds.join(',') : undefined
    }),
    enabled: isUnified && accessibleStoreIds.length > 0
  })

  // Fetch agents for filter
  const { data: agentsData } = useQuery({
    queryKey: ['agents', activeStoreId || accessibleStoreIds[0]],
    queryFn: () => storeApi.getAgents(activeStoreId || accessibleStoreIds[0]),
    enabled: !isUnified && !!activeStoreId
  })

  const conversations = conversationsData?.data.data.conversations || []
  const pagination = conversationsData?.data.data.pagination
  const agents = agentsData?.data.data.agents || []
  const stats = statsData?.data.data

  // Build store counts for filter
  const storeCounts = useMemo(() => {
    if (!stats?.byStore) return {}
    return stats.byStore.reduce((acc: Record<string, number>, item: any) => {
      acc[item.storeId] = item.conversationCount
      return acc
    }, {})
  }, [stats])

  // Status options
  const statusOptions = [
    { label: 'All Status', value: '' },
    { label: 'New', value: 'NEW' },
    { label: 'Assigned', value: 'ASSIGNED' },
    { label: 'In Progress', value: 'IN_PROGRESS' },
    { label: 'Resolved', value: 'RESOLVED' },
    { label: 'Closed', value: 'CLOSED' }
  ]

  // Agent options
  const agentOptions = [
    { label: 'All Agents', value: '' },
    ...agents.map(agent => ({ label: agent.name, value: agent.id }))
  ]

  // Handlers
  const handleFiltersChange = (newFilters: any) => {
    setFilters(prev => ({ ...prev, ...newFilters, page: 1 }))
  }

  const handleSearchChange = (value: string) => {
    setSearchValue(value)
    setFilters(prev => ({ ...prev, search: value, page: 1 }))
  }

  const handlePageChange = (page: number) => {
    setFilters(prev => ({ ...prev, page }))
  }

  const handleStoreFilterChange = (storeIds: string[]) => {
    setSelectedStoreIds(storeIds)
    setFilters(prev => ({ ...prev, page: 1 }))
  }

  // Status badge helper
  const getStatusBadge = (status: ConversationStatus) => {
    const statusMap = {
      NEW: { status: 'info' as const, label: 'New' },
      ASSIGNED: { status: 'attention' as const, label: 'Assigned' },
      IN_PROGRESS: { status: 'warning' as const, label: 'In Progress' },
      RESOLVED: { status: 'success' as const, label: 'Resolved' },
      CLOSED: { status: 'subdued' as const, label: 'Closed' },
      ARCHIVED: { status: 'subdued' as const, label: 'Archived' }
    }

    const config = statusMap[status] || statusMap.NEW
    return <Badge status={config.status}>{config.label}</Badge>
  }

  // Render conversation item
  const renderConversationItem = (conversation: any) => {
    const lastMessage = conversation.messages?.[0]
    const unreadCount = conversation._count?.messages || 0
    const store = conversation.store

    return (
      <ResourceItem
        id={conversation.id}
        onClick={() => navigate(`/conversations/${conversation.id}`)}
        accessibilityLabel={`Conversation with ${conversation.customer?.name || conversation.customer?.whatsappPhoneNumber}`}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <Avatar
            customer
            name={conversation.customer?.name || conversation.customer?.whatsappPhoneNumber}
            size="medium"
          />

          <div style={{ flex: 1 }}>
            <InlineStack align="space-between" blockAlign="center">
              <BlockStack gap="0">
                <InlineStack gap="200" align="start" blockAlign="center">
                  <Text variant="bodyMd" fontWeight="semibold" as="h3">
                    {conversation.customer?.name || conversation.customer?.whatsappPhoneNumber}
                  </Text>
                  {/* Show store badge in unified view */}
                  {isUnified && store && (
                    <Badge status="info">
                      {store.shopDomain || store.platformDomain || 'Store'}
                    </Badge>
                  )}
                </InlineStack>
                <Text variant="bodySm" color="subdued" as="p">
                  {lastMessage?.content || 'No messages yet'}
                </Text>
              </BlockStack>

              <div style={{ textAlign: 'right' }}>
                <div style={{ marginBottom: '4px' }}>
                  {getStatusBadge(conversation.status)}
                </div>
                {conversation.lastMessageAt && (
                  <Text variant="bodySm" color="subdued" as="p">
                    {format(new Date(conversation.lastMessageAt), 'MMM d, HH:mm')}
                  </Text>
                )}
                {unreadCount > 0 && (
                  <Badge status="critical">{unreadCount} unread</Badge>
                )}
              </div>
            </InlineStack>

            {conversation.assignedAgent && (
              <div style={{ marginTop: '8px' }}>
                <Text variant="bodySm" color="subdued" as="p">
                  Assigned to: {conversation.assignedAgent.name}
                </Text>
              </div>
            )}

            {conversation.order && (
              <div style={{ marginTop: '4px' }}>
                <Badge>Order #{conversation.order.orderNumber}</Badge>
              </div>
            )}
          </div>
        </div>
      </ResourceItem>
    )
  }

  // Applied filters
  const appliedFilters = []
  if (filters.status) {
    appliedFilters.push({
      key: 'status',
      label: `Status: ${statusOptions.find(opt => opt.value === filters.status)?.label}`,
      onRemove: () => handleFiltersChange({ status: undefined })
    })
  }
  if (filters.assignedAgentId) {
    appliedFilters.push({
      key: 'agent',
      label: `Agent: ${agentOptions.find(opt => opt.value === filters.assignedAgentId)?.label}`,
      onRemove: () => handleFiltersChange({ assignedAgentId: undefined })
    })
  }
  if (selectedStoreIds.length > 0 && selectedStoreIds.length < stores.length) {
    appliedFilters.push({
      key: 'stores',
      label: `${selectedStoreIds.length} account${selectedStoreIds.length > 1 ? 's' : ''} selected`,
      onRemove: () => setSelectedStoreIds([])
    })
  }

  // Filter control
  const filterControl = (
    <Filters
      queryValue={searchValue}
      filters={[
        {
          key: 'status',
          label: 'Status',
          filter: (
            <Select
              label="Status"
              options={statusOptions}
              value={filters.status || ''}
              onChange={(value) => handleFiltersChange({ status: value || undefined })}
            />
          )
        },
        ...(isUnified ? [] : [{
          key: 'agent',
          label: 'Assigned Agent',
          filter: (
            <Select
              label="Assigned Agent"
              options={agentOptions}
              value={filters.assignedAgentId || ''}
              onChange={(value) => handleFiltersChange({ assignedAgentId: value || undefined })}
            />
          )
        }])
      ]}
      appliedFilters={appliedFilters}
      onQueryChange={handleSearchChange}
      onQueryClear={() => handleSearchChange('')}
      onClearAll={() => {
        setSearchValue('')
        setSelectedStoreIds([])
        setFilters({
          storeId: activeStoreId || undefined,
          page: 1,
          limit: 20
        })
      }}
    >
      <div style={{ paddingLeft: '8px' }}>
        <TextField
          label=""
          value={searchValue}
          onChange={handleSearchChange}
          placeholder="Search conversations..."
          clearButton
          onClearButtonClick={() => handleSearchChange('')}
        />
      </div>
    </Filters>
  )

  // Loading state
  if (isLoading) {
    return (
      <Page title="Conversations">
        <Layout>
          <Layout.Section>
            <div style={{ textAlign: 'center', padding: '60px 0' }}>
              <Spinner accessibilityLabel="Loading conversations" size="large" />
            </div>
          </Layout.Section>
        </Layout>
      </Page>
    )
  }

  // Page title and subtitle
  const pageTitle = isUnified ? 'All Conversations' : 'Conversations'
  const pageSubtitle = isUnified
    ? `${pagination?.total || 0} conversations across ${selectedStoreIds.length > 0 ? selectedStoreIds.length : stores.length} account${(selectedStoreIds.length || stores.length) > 1 ? 's' : ''}`
    : `${pagination?.total || 0} conversations`

  return (
    <Page
      title={pageTitle}
      subtitle={pageSubtitle}
      titleMetadata={hasMultipleStores ? <StoreSwitcher variant="compact" /> : undefined}
    >
      <Layout>
        {/* Store filter sidebar in unified view */}
        {isUnified && hasMultipleStores && (
          <Layout.Section secondary>
            <StoreFilter
              selectedStoreIds={selectedStoreIds}
              onChange={handleStoreFilterChange}
              showCount
              storeCounts={storeCounts}
            />

            {/* Unified stats */}
            {stats && (
              <Card>
                <BlockStack gap="300">
                  <Text variant="headingSm" as="h3">
                    Overview
                  </Text>
                  <InlineStack gap="400" wrap>
                    <BlockStack gap="0">
                      <Text variant="headingLg" as="p">{stats.total}</Text>
                      <Text variant="bodySm" color="subdued" as="p">Total</Text>
                    </BlockStack>
                    <BlockStack gap="0">
                      <Text variant="headingLg" as="p">{stats.totalUnread}</Text>
                      <Text variant="bodySm" color="subdued" as="p">Unread</Text>
                    </BlockStack>
                    <BlockStack gap="0">
                      <Text variant="headingLg" as="p">{stats.byStatus?.new || 0}</Text>
                      <Text variant="bodySm" color="subdued" as="p">New</Text>
                    </BlockStack>
                  </InlineStack>
                </BlockStack>
              </Card>
            )}
          </Layout.Section>
        )}

        <Layout.Section>
          {/* Unified view banner */}
          {isUnified && (
            <div style={{ marginBottom: '16px' }}>
              <Banner
                status="info"
                icon={GlobeMajor}
              >
                <p>Viewing conversations from all accounts. Click on a store badge to filter.</p>
              </Banner>
            </div>
          )}

          <Card>
            <ResourceList
              resourceName={{ singular: 'conversation', plural: 'conversations' }}
              items={conversations}
              renderItem={renderConversationItem}
              filterControl={filterControl}
              emptyState={
                <div style={{ textAlign: 'center', padding: '60px 0' }}>
                  <Text variant="headingMd" as="h3">
                    No conversations found
                  </Text>
                  <Text variant="bodyMd" as="p" color="subdued">
                    {searchValue || filters.status || filters.assignedAgentId || selectedStoreIds.length > 0
                      ? 'Try adjusting your search or filters'
                      : 'Conversations will appear here when customers message you on WhatsApp'
                    }
                  </Text>
                </div>
              }
            />

            {pagination && pagination.pages > 1 && (
              <div style={{
                display: 'flex',
                justifyContent: 'center',
                padding: '16px'
              }}>
                <Pagination
                  hasPrevious={pagination.page > 1}
                  onPrevious={() => handlePageChange(pagination.page - 1)}
                  hasNext={pagination.page < pagination.pages}
                  onNext={() => handlePageChange(pagination.page + 1)}
                  label={`Page ${pagination.page} of ${pagination.pages}`}
                />
              </div>
            )}
          </Card>
        </Layout.Section>
      </Layout>
    </Page>
  )
}
